import React, { useRef, useState, useEffect, useCallback } from "react"
import {
  useElementResizing,
  ElementDimensionUpdate,
  MinDimensions,
} from "features/Reporting/hooks/Elements/useElementResizing"
import { SlideElement } from "types/ReportingDashboard/elements.types"

/**
 * Configuration for which resize handles to display
 */
export interface HandlesConfiguration {
  horizontal?: boolean
  vertical?: boolean
  corner?: boolean
}

/**
 * Style type for resize handles
 */
export type HandleStyleType = "visible" | "zone"

/**
 * Element update payload for dimension changes - extends the hook's interface
 */
export interface ElementUpdate extends ElementDimensionUpdate {
  [key: string]: unknown
}

/**
 * Hook to handle canvas-responsive scaling for all elements
 */
const useCanvasResponsiveScaling = () => {
  const [canvasScale, setCanvasScale] = useState(1)
  const canvasRef = useRef<HTMLElement | null>(null)
  const lastScaleRef = useRef(1)

  const updateScale = useCallback(() => {
    const canvas =
      canvasRef.current ||
      (document.querySelector('[aria-label="Reporting Canvas"]') as HTMLElement)

    if (!canvas) return

    canvasRef.current = canvas
    const rect = canvas.getBoundingClientRect()

    // Calculate scale based on canvas width (baseline: 1440px)
    const widthScale = rect.width / 1440

    // Only update if scale changed significantly (prevents unnecessary re-renders)
    if (Math.abs(widthScale - lastScaleRef.current) > 0.01) {
      setCanvasScale(widthScale)
      lastScaleRef.current = widthScale
    }
  }, [])

  useEffect(() => {
    // Initial scale calculation
    updateScale()

    // Watch for canvas size changes
    const resizeObserver = new ResizeObserver(updateScale)

    const canvas = document.querySelector(
      '[aria-label="Reporting Canvas"]'
    ) as HTMLElement
    if (canvas) {
      canvasRef.current = canvas
      resizeObserver.observe(canvas)
    }

    // Fallback: window resize listener
    window.addEventListener("resize", updateScale)

    // Also watch for canvas appearing/changing (if it's dynamically rendered)
    const mutationObserver = new MutationObserver(updateScale)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener("resize", updateScale)
    }
  }, [updateScale])

  return {
    canvasScale,
    isScaled: Math.abs(canvasScale - 1) > 0.05, // 5% threshold for "scaled"
  }
}

/**
 * Props for ResizableElementWrapper component
 */
export interface ResizableElementWrapperProps {
  /** The slide element to make resizable */
  element: SlideElement
  /** Callback to update element properties */
  onUpdate: (elementId: string, updates: ElementUpdate) => void
  /** Whether the element is in readonly mode */
  readonly?: boolean
  /** Minimum dimensions for resizing */
  minDimensions?: MinDimensions
  /** Default width if not specified in element */
  defaultWidth: number
  /** Default height if not specified in element */
  defaultHeight: number
  /** Whether to maintain aspect ratio during resize */
  maintainAspectRatio?: boolean
  /** Specific aspect ratio to maintain (height/width) */
  aspectRatio?: number
  /** Callback to set skip update flag during resize */
  setSkipUpdateFlag?: (skip: boolean) => void
  /** Click handler for the wrapper */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  /** Additional CSS classes to apply */
  additionalClasses?: string
  /** Style overrides for the wrapper */
  styleOverrides?: React.CSSProperties
  /** ARIA label for accessibility */
  ariaLabel?: string
  /** ARIA readonly state for accessibility */
  ariaReadonly?: boolean
  /** Child components to render inside the wrapper */
  children: React.ReactNode
  /** Style type for resize handles */
  handleStyleType?: HandleStyleType
  /** Configuration for which handles to show */
  handlesConfiguration?: HandlesConfiguration
  /** Whether to enable responsive scaling (default: true) */
  enableResponsiveScaling?: boolean
}

/**
 * A wrapper component that provides resizing capabilities and responsive scaling to its children.
 * Supports both visible and invisible resize handles with customizable configuration.
 * Automatically scales all elements with the canvas for Canva-like behavior.
 *
 * @param props - Component props
 * @returns JSX element with resizing capabilities and responsive scaling
 */
const ResizableElementWrapper: React.FC<ResizableElementWrapperProps> = ({
  element,
  onUpdate,
  readonly = false,
  minDimensions,
  defaultWidth,
  defaultHeight,
  maintainAspectRatio = false,
  aspectRatio,
  setSkipUpdateFlag,
  onClick,
  additionalClasses = "",
  styleOverrides = {},
  ariaLabel,
  ariaReadonly,
  children,
  handleStyleType = "zone",
  handlesConfiguration = { horizontal: true, vertical: true, corner: true },
  enableResponsiveScaling = true,
}) => {
  const wrapperRef = useRef<HTMLElement>(null)

  // Get canvas scaling information
  const { canvasScale, isScaled } = useCanvasResponsiveScaling()

  const {
    handleHorizontalResizeStart,
    handleVerticalResizeStart,
    handleCornerResizeStart,
  } = useElementResizing({
    elementId: element.id,
    currentWidth: element.width || defaultWidth,
    currentHeight: element.height || defaultHeight,
    onUpdate: (elementId: string, updates: ElementDimensionUpdate) => {
      // Convert ElementDimensionUpdate to ElementUpdate to satisfy our interface
      onUpdate(elementId, updates as ElementUpdate)
    },
    setSkipUpdateFlag: setSkipUpdateFlag,
    minDimensions,
    wrapperRef: wrapperRef,
    maintainAspectRatio,
    aspectRatio,
  })

  const baseClasses = "relative group"
  const hoverOutlineClass = !readonly
    ? "hover:outline hover:outline-1 hover:outline-blue-400 hover:outline-dashed"
    : ""

  const combinedClasses =
    `${baseClasses} ${hoverOutlineClass} ${additionalClasses}`.trim()

  // Calculate responsive wrapper style - FIXED APPROACH
  const wrapperStyle: React.CSSProperties = {
    width: element.width || defaultWidth,
    height: element.height || defaultHeight,
    // REVERTED: Use transform with better event handling approach
    ...(enableResponsiveScaling && isScaled
      ? {
          transform: `scale(${canvasScale})`,
          transformOrigin: "top left",
          // Prevent layout shifts by adjusting container size
          marginRight: `${
            (element.width || defaultWidth) * (canvasScale - 1)
          }px`,
          marginBottom: `${
            (element.height || defaultHeight) * (canvasScale - 1)
          }px`,
        }
      : {}),
    ...styleOverrides,
  }

  const zoneBase = "resize-zone absolute z-20"
  const zoneThickness = "8px"
  const visibleHandleBase =
    "absolute z-30 opacity-20 group-hover:opacity-100 transition-opacity"
  const visibleHandleColor = "rgba(113, 128, 150, 0.5)"

  /**
   * Renders resize handles based on configuration and style type
   */
  const renderHandles = (): React.ReactNode => {
    if (readonly) return null

    const config: Required<HandlesConfiguration> = {
      horizontal:
        handlesConfiguration.horizontal !== undefined
          ? handlesConfiguration.horizontal
          : true,
      vertical:
        handlesConfiguration.vertical !== undefined
          ? handlesConfiguration.vertical
          : true,
      corner:
        handlesConfiguration.corner !== undefined
          ? handlesConfiguration.corner
          : true,
    }

    // FIXED: Remove complex scaling logic for handles - let CSS zoom handle it
    if (handleStyleType === "visible") {
      return (
        <>
          {config.horizontal && (
            <div
              className={`${visibleHandleBase} top-1/2 right-0 cursor-ew-resize`}
              style={{
                background: visibleHandleColor,
                borderRadius: "2px",
                width: "8px",
                height: "64px",
                transform: "translateY(-50%)",
              }}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()
                handleHorizontalResizeStart(e)
              }}
              aria-label="Resize horizontally"
            />
          )}
          {config.vertical && (
            <div
              className={`${visibleHandleBase} bottom-0 left-1/2 cursor-ns-resize`}
              style={{
                background: visibleHandleColor,
                borderRadius: "2px",
                width: "64px",
                height: "8px",
                transform: "translateX(-50%)",
              }}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()
                handleVerticalResizeStart(e)
              }}
              aria-label="Resize vertically"
            />
          )}
          {config.corner && (
            <div
              className={`${visibleHandleBase} bottom-0 right-0 cursor-nwse-resize`}
              style={{
                background: "rgba(113, 128, 150, 0.6)",
                borderTopLeftRadius: "4px",
                width: "16px",
                height: "16px",
              }}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()
                handleCornerResizeStart(e)
              }}
              aria-label="Resize diagonally"
            />
          )}
        </>
      )
    }

    // Default to "zone" style (invisible handles)
    return (
      <>
        {config.horizontal && (
          <div
            className={`${zoneBase} cursor-ew-resize top-0 right-0 h-full`}
            style={{ width: zoneThickness }}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation()
              handleHorizontalResizeStart(e)
            }}
            aria-label="Resize horizontally"
          />
        )}
        {config.vertical && (
          <div
            className={`${zoneBase} cursor-ns-resize bottom-0 left-0 w-full`}
            style={{ height: zoneThickness }}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation()
              handleVerticalResizeStart(e)
            }}
            aria-label="Resize vertically"
          />
        )}
        {config.corner && (
          <div
            className={`${zoneBase} cursor-nwse-resize bottom-0 right-0`}
            style={{ width: zoneThickness, height: zoneThickness }}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation()
              handleCornerResizeStart(e)
            }}
            aria-label="Resize diagonally"
          />
        )}
      </>
    )
  }

  return (
    <div
      ref={wrapperRef as React.RefObject<HTMLDivElement>}
      className={combinedClasses}
      style={wrapperStyle}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-readonly={ariaReadonly ? "true" : undefined}
    >
      <style>{`.is-resizing-outline { outline: 1px dashed #3b82f6 !important; outline-offset: 1px; }`}</style>
      {children}
      {renderHandles()}
    </div>
  )
}

export default ResizableElementWrapper
